import { Link } from '@tanstack/react-router';

import { AppNav } from './AppNav.tsx';
import { WorkspaceContainer } from './WorkspaceLayout.tsx';
import { useLogout, useMe } from '../lib/auth.ts';

export interface AuthenticatedMobileShellProps {
  children: React.ReactNode;
  currentPath: string;
}

export function AuthenticatedMobileShell({
  children,
  currentPath,
}: AuthenticatedMobileShellProps): React.ReactElement {
  const me = useMe();
  const logout = useLogout();
  const displayName = me.data?.user.displayName ?? me.data?.user.login ?? 'Owner';

  return (
    <div className="min-h-screen pb-[calc(5.5rem+env(safe-area-inset-bottom))] sm:pb-0">
      <header className="hidden border-b border-line bg-paper-soft/85 backdrop-blur sm:block">
        <WorkspaceContainer className="flex min-h-16 items-center gap-4 py-3">
          <Link
            to="/"
            className="group flex shrink-0 items-baseline gap-2 text-ink transition hover:text-ochre-deep"
          >
            <span
              aria-hidden="true"
              className="font-serif text-lg italic leading-none text-ochre transition group-hover:text-ochre-deep"
            >
              §
            </span>
            <span className="font-serif text-lg font-medium italic tracking-tight">
              Invoice Generator
            </span>
          </Link>
          <div className="min-w-0 flex-1">
            <AppNav variant="top" currentPath={currentPath} />
          </div>
          <Link
            to="/invoices/new"
            aria-current={currentPath === '/invoices/new' ? 'page' : undefined}
            className="inline-flex min-h-10 shrink-0 items-center justify-center rounded-full bg-ochre px-4 py-2 text-sm font-semibold text-paper-soft shadow-[0_6px_18px_-10px_rgba(176,119,43,0.85)] transition hover:bg-ochre-deep"
          >
            New invoice
          </Link>
          <div className="flex min-w-0 shrink-0 items-center gap-3">
            <span className="max-w-40 truncate text-sm text-ink-soft">{displayName}</span>
            <button
              type="button"
              onClick={() => logout.mutate()}
              disabled={logout.isPending}
              className="inline-flex min-h-10 items-center justify-center rounded-full border border-line bg-paper px-3 py-2 text-sm font-medium text-ink transition hover:border-ochre/40 hover:bg-paper-deep disabled:cursor-not-allowed disabled:opacity-60"
            >
              {logout.isPending ? 'Logging out…' : 'Log out'}
            </button>
          </div>
        </WorkspaceContainer>
        {logout.isError ? (
          <WorkspaceContainer className="pb-3">
            <p role="alert" className="text-sm text-clay">
              Could not log out. Try again.
            </p>
          </WorkspaceContainer>
        ) : null}
      </header>
      {children}
      <AppNav variant="mobile-bottom" currentPath={currentPath} />
    </div>
  );
}
